/**
 * The transitional AngularJS <-> React contract for switching venue.
 *
 * AngularJS still owns the currently active company, so it remains the
 * executor of the actual switch (see the handler in
 * `app/startup/reactBridge.run.js`). React owns the persisted selection and
 * hands off to AngularJS via the bridge events below. Once AngularJS has done
 * it's thing it will update the app context, which the React app then responds
 * to and updates to the newly updated company.
 *
 * Once we move the retrieval / ownership of this active venue data fully to
 * React we will be able to update this bridge to instead be telling AngularJS
 * about the newly selected venue details directly.
 */

/**
 * Bridge event the React topbar dispatches to ask the AngularJS app to switch
 * the active venue.
 */
export const REACT_VENUE_CHANGE_EVENT = '@@loaded/venueChange'

/**
 * Bridge event the React topbar dispatches when the multisite venue
 * multi-select changes. React owns the selection and has already persisted it
 * to `@@loaded/multisiteSelectedCompanies`; this event carries no payload and
 * exists only to tell AngularJS the selection moved, so it re-digests and the
 * open multisite report rebuilds (reading the new value straight from storage).
 */
export const REACT_MULTISITE_SELECTION_CHANGE_EVENT =
  '@@loaded/multisiteSelectionChange'

/**
 * Bridge event AngularJS dispatches whenever the app context data changes
 * (see app/startup/reactBridge.run.js). A detail type of 'company' means the
 * active company data as the result of a venue switch has completed and
 * therefore the React app should re-render.
 */
export const APP_CONTEXT_CHANGE_EVENT = '@@loaded/appContextChange'

/**
 * Shared storage keys read by the AngularJS `userinfo` service on bootstrap to
 * decide the active venue. React writes them here so that, after a switch, a
 * fresh tab/refresh resolves to the venue the user picked. The values must match
 * exactly what AngularJS expects.
 */
const SESSION_ACTIVE_COMPANY_KEY = 'activeCompany'
const LOCAL_DEFAULT_COMPANY_KEY = 'ls.defaultCompany'

/**
 * How long single-venue mode waits for AngularJS to confirm a venue switch
 * (via an `appContextChange` of type 'company') before it clears the transient
 * "Switching ..." state on its own, so a dropped/failed handoff can never leave
 * the trigger stuck disabled.
 */
export const SWITCHING_FALLBACK_TIMEOUT_MS = 10000

/**
 * Persists the selected venue id to the storage keys shared with AngularJS,
 * matching AngularJS's exact key names and value formats so a refresh resolves
 * to the same venue. React becomes the source of truth for these keys. The data
 * reads etc and updating the app context is still handled by AngularJS.
 */
export function writeSharedVenueStorage(venueId: string): void {
  // Raw string, mirroring `userinfo.activeCompany`'s `sessionStorage.activeCompany = companyId`.
  sessionStorage.setItem(SESSION_ACTIVE_COMPANY_KEY, venueId)

  // JSON-encoded, mirroring `localStorageService.set('defaultCompany', companyId)`
  // (angular-local-storage `set` runs the value through `angular.toJson`).
  localStorage.setItem(LOCAL_DEFAULT_COMPANY_KEY, JSON.stringify(venueId))
}
